/* eslint-disable @typescript-eslint/no-explicit-any -- any is useful for generic interfaces */

import { Draft } from "immer";
import { z } from "zod";

import { AppSessionEvent } from "./AppSessionEvent.js";
import {
  AppSessionEventState,
  AppSessionEventStateIndices,
} from "./AppSessionEventState.js";

/**
 * Actual definition of a specific app session event.
 *
 * Contains metadata and helper for the event, such as how to update the state,
 * how to safely log the event, etc
 */
export interface AppSessionEventDefinition<
  EVENT extends AppSessionEvent<string, any>,
  CREATE_ARGS extends any[],
> {
  /**
   * Used as the discriminant for the event itself
   */
  type: EVENT["type"];

  /**
   * Schema for the event's payload as it is sent over the wire and persisted.
   */
  payloadSchema: z.ZodType<EVENT["payload"], z.ZodTypeDef, any>;

  /**
   * Which keys of type `PAYLOAD` whose values are safe to log.
   *
   * Can also be a function that takes an event and produces a record of log safe values.
   */
  logSafe:
    | Record<keyof EVENT["payload"], boolean>
    | ((args: {
        event: EVENT;
        dangerouslyUnsafe: boolean;
      }) => Record<string, unknown>);

  /**
   * Helper for creating events of this type.
   */
  create: (...args: CREATE_ARGS) => EVENT;

  /**
   * How to actually update the state once this event has been received.
   *
   * Uses [immer](https://immerjs.github.io/immer/) to mutate the state/indices
   */
  apply: (args: {
    state: Draft<AppSessionEventState>;
    indices: Draft<AppSessionEventStateIndices>;
    payload: EVENT["payload"];
  }) => void | { warnings: string[] };
}

/**
 * Helper function that makes it easier to define app session events
 */
export const createAppSessionEventDefinition = <
  TYPE extends string,
  PAYLOAD_SCHEMA extends z.ZodObject<any, any>,
  CREATE_ARGS extends any[],
>({
  apply,
  create,
  logSafe,
  payloadSchema,
  type,
}: {
  type: TYPE;
  payloadSchema: PAYLOAD_SCHEMA;
  logSafe: AppSessionEventDefinition<
    AppSessionEvent<TYPE, z.infer<PAYLOAD_SCHEMA>>,
    CREATE_ARGS
  >["logSafe"];
  create: (...args: CREATE_ARGS) => z.infer<PAYLOAD_SCHEMA>;
  apply: (args: {
    state: Draft<AppSessionEventState>;
    indices: Draft<AppSessionEventStateIndices>;
    payload: z.infer<PAYLOAD_SCHEMA>;
  }) => void | { warnings: string[] };
}): AppSessionEventDefinition<
  AppSessionEvent<TYPE, z.infer<PAYLOAD_SCHEMA>>,
  CREATE_ARGS
> => ({
  type,
  payloadSchema,
  logSafe,
  create: (...args: CREATE_ARGS) => ({ type, payload: create(...args) }),
  apply,
});
